import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ApplicationDestination } from '../selectors/application-destination';
import ButtonLink from './ButtonLink';

import Link from '@atlaskit/link';
import { getIdacOrigin } from '../utilities/env';

export const SELF_SERVICE_LINK_URL =
  'https://support.atlassian.com/atlassian-account/docs/troubleshoot-login-issues-with-your-atlassian-account/';

export function SelfServiceLink(props: { onClick?: () => void }) {
  return (
    <Link
      href={SELF_SERVICE_LINK_URL}
      id="self-service-link"
      target="_blank"
      rel="noopener noreferrer"
      // https://mathiasbynens.github.io/rel-noopener
      onClick={props.onClick}>
      <FormattedMessage id="resetpassword.self.service" defaultMessage="Login help" />
    </Link>
  );
}

export const SUPPORT_LINK_URL = 'https://support.atlassian.com/';

export function SupportLink(props: { onClick?: () => void }) {
  return (
    <Link
      href={SUPPORT_LINK_URL}
      id="support-link"
      target="_blank"
      rel="noopener noreferrer"
      // https://mathiasbynens.github.io/rel-noopener
      onClick={props.onClick}>
      <FormattedMessage id="resetpassword.support" defaultMessage="Contact Support" />
    </Link>
  );
}

// Used for both signup and login flows
export const PRIVACY_POLICY_URL_MOBILE = 'https://www.atlassian.com/legal/privacy-policy/mobile';
export const PRIVACY_POLICY_URL = 'https://www.atlassian.com/legal/privacy-policy';

export function PrivacyPolicyLink(props: { isMobileLink: boolean }) {
  const { formatMessage } = useIntl();
  const privacyPolicyUrl = props.isMobileLink ? PRIVACY_POLICY_URL_MOBILE : PRIVACY_POLICY_URL;

  return (
    <Link
      href={privacyPolicyUrl}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={formatMessage({
        id: 'common.atlassian.privacy.policy.link.aria.label',
        defaultMessage: 'Atlassian Privacy Policy',
        description:
          "Aria label for link to let users learn more about Atlassian's Privacy Policy.",
      })}
      // https://mathiasbynens.github.io/rel-noopener
    >
      <FormattedMessage id="common.privacy.policy" defaultMessage="Privacy Policy" />
    </Link>
  );
}

// Used for signup flows only
export const CLOUD_TERMS_OF_SERVICE_URL_MOBILE =
  'https://www.atlassian.com/legal/customer-agreement/mobile';
export const CLOUD_TERMS_OF_SERVICE_URL = 'https://www.atlassian.com/legal/cloud-terms-of-service';

// Used for login flows only
export const USER_NOTICE_URL_MOBILE = 'https://www.atlassian.com/legal/user-notice/mobile';
export const USER_NOTICE_URL = 'https://www.atlassian.com/legal/user-notice';

export function UserNoticeLink(props: { isMobileLink: boolean }) {
  const { formatMessage } = useIntl();
  const userNoticeUrl = props.isMobileLink ? USER_NOTICE_URL_MOBILE : USER_NOTICE_URL;

  return (
    <Link
      href={userNoticeUrl}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={formatMessage({
        id: 'common.atlassian.user.notice.link.aria.label',
        defaultMessage: 'Atlassian User Notice',
        description: "Aria label for link to let users learn more about Atlassian's User Notice.",
      })}
      // https://mathiasbynens.github.io/rel-noopener
    >
      <FormattedMessage
        id="common.user.notice"
        defaultMessage="User Notice"
        description="Link that takes users to the Atlassian User Notice"
      />
    </Link>
  );
}

export function RequestAccessLink(props: {
  onClick?: () => void;
  destUrl: string;
  applicationDestination: ApplicationDestination;
}) {
  const { destUrl, applicationDestination } = props;
  const continueUrl = encodeURIComponent(
    `https://${
      applicationDestination.destinationHostname
    }/login?application=${applicationDestination.applicationName.toLowerCase()}` +
      '&permission-violation=true' +
      '&preferred-redirect=request-access' +
      (destUrl ? `&dest-url=${encodeURIComponent(destUrl)}` : '')
  );
  const redirectUrl = `${getIdacOrigin()}/login?continue=${continueUrl}`;
  const buttonText = (
    <FormattedMessage
      id="select.account.request.access.to.product"
      defaultMessage="Request access to {productName}"
      values={{ productName: applicationDestination.applicationName }}
    />
  );
  return (
    <ButtonLink id="redirect-to-request-access" href={redirectUrl} onClick={props.onClick}>
      {buttonText}
    </ButtonLink>
  );
}

export const PASSWORD_LEAKED_LINK =
  'https://support.atlassian.com/atlassian-account/docs/protect-your-atlassian-account/';
